import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import { ShapesDecorativeBorder, ButtonLink, OtpInput } from 'src/components';
import {
  onboardingSetPasswordRoute,
  onboardingResetPasswordResendOtpRoute,
} from 'src/routing/routes';
import { getMerchantId, setOtpForSession } from 'src/services/user.service';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import TealiumEvents from 'src/helpers/tealium-events';
import ButtonPrimary from '../component/button-primary/button-primary';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { FormContainer } from '../../form-container/form-container';
import { SelfServiceFormBackground } from '../../self-service-form-background/self-service-form-background';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import useBrokerLogin from 'src/hooks/useBrokerLogin';
import './broker-forgot-password-otp.scss';

export default function BrokerForgotPasswordOtp() {
  const otpLength = 4;
  const otpTriesMax = 3;
  const [otpTries, setOtpTries] = useState(0);
  const [redirectToRoute, setRedirectToRoute] = useState('');
  const otpTriesExceeded = () => otpTries >= otpTriesMax;
  const handleTechnicalError = useTechnicalErrorHandler();
  const { authenticate } = useBrokerLogin();

  const handleIncorrectOTPError = () => {
    TealiumEvents.onHandledErrorEventSelfService('OTP validation failed - incorrect OTP');

    const otpRetriesCurrent = otpTries + 1;
    setOtpTries(otpRetriesCurrent);

    if (otpRetriesCurrent >= otpTriesMax) {
      TealiumEvents.onHandledErrorFormSelfService('OTP attempts exceeded');
      toastMessageServiceSingleton.addError(
        'You’ve exceeded the OTP attempts',
        null,
        '401',
        true,
        null,
        true,
      );
    } else {
      TealiumEvents.onHandledErrorFormSelfService('OTP validation failed - incorrect OTP');
      toastMessageServiceSingleton.addError(
        `Incorrect OTP - Try again ${otpRetriesCurrent}/${otpTriesMax}`,
        null,
        '401',
        true,
        null,
        true,
      );
    }
  };

  useEffect(() => {
    TealiumEvents.loginOTPPageLoadNewStyle();
  }, []);

  if (!!redirectToRoute) {
    return <Redirect push to={redirectToRoute} />;
  }

  return (
    <div className="broker-forgot-password-otp">
      <SelfServiceFormBackground className={'dark-background'}>
        <ShapesDecorativeBorder>
          <FormContainer classname={'container-white'}>
            <Formik initialValues={{ merchantOtp: '' }} onSubmit={submitOtp}>
              {({ isSubmitting, values }) => (
                <Form>
                  <div className="has-text-centered">
                    <div className="heading-we-sent-you-an-email heading-four">
                      We sent you an sms
                    </div>

                    <p className="sub-heading-please-enter-otp heading-six">
                      Please enter 4 digit verification code sent to your phone
                    </p>
                  </div>
                  <div className="otp-centered" automationid="Reset_Password_Div_OTP">
                    <Field
                      key={otpTries}
                      name="merchantOtp"
                      component={MiniAppFocusWrapper}
                      innerComponent={OtpInput}
                      numInputs={otpLength}
                      shouldAutoFocus={true}
                      autoFocus
                      isDisabled={otpTriesExceeded()}
                    />
                  </div>
                  <ButtonPrimary
                    automationid="Reset_Password_Button_Continue"
                    type="submit"
                    disabled={
                      isSubmitting || values.merchantOtp.length < otpLength || otpTriesExceeded()
                    }
                    className="confirm-button"
                  >
                    Continue
                  </ButtonPrimary>
                  <div className="reset-text has-text-centered">
                    <span className="request-reset-left content-normal">
                      Didn’t receive an OTP?
                    </span>
                    &nbsp;
                    <ButtonLink
                      className="request-reset-right"
                      onClick={() => {
                        if (!otpTriesExceeded() && !isSubmitting) {
                          TealiumEvents.loginOTPClickResend(onboardingResetPasswordResendOtpRoute);
                          setRedirectToRoute(onboardingResetPasswordResendOtpRoute);
                        }
                      }}
                      disabled={isSubmitting || otpTriesExceeded()}
                    >
                      Resend
                    </ButtonLink>
                  </div>
                </Form>
              )}
            </Formik>
          </FormContainer>
        </ShapesDecorativeBorder>
      </SelfServiceFormBackground>
    </div>
  );

  async function submitOtp(values, helpers) {
    const { setSubmitting, resetForm } = helpers;
    setSubmitting(true);

    const { error } = await authenticate({
      merchantId: getMerchantId(),
      password: values.merchantOtp,
    });

    setSubmitting(false);
    switch (true) {
      case error === null:
      case error === undefined:
        TealiumEvents.loginOTPClickContinue(onboardingSetPasswordRoute);
        setOtpForSession(values.merchantOtp);
        setRedirectToRoute(onboardingSetPasswordRoute);
        break;
      case error && error.includes('Your password or merchant ID is incorrect'):
        handleIncorrectOTPError();
        resetForm();
        break;
      default:
        handleTechnicalError('unrecognized error received in response to OTP get request');
        break;
    }
  }
}
